import Ably from 'ably';

import {ServerDocumentEditorLock} from 'notebook/realtime/DocumentEditorLockConstants';
import {DocumentUpdateNotification} from 'notebook/realtime/DocumentUpdateNotifications';
import ErrorSubclass from 'util/ErrorSubclass';

import {RealtimeMessageType} from './Constants';

export default interface RealtimeChannel {
  presence: RealtimeChannelPresence;
  subscribe(
    handler: (message: RealtimeMessage | EncryptedRealtimeMessage) => void,
    subscribeCallback?: (err?: RealtimeError | null) => void
  ): void;
  unsubscribe(): void;
  stop(): void;
}

export interface RealtimeChannelPresence {
  enter(callback?: (err?: RealtimeError | null) => void): void;
  leave(callback?: (err?: RealtimeError | null) => void): void;
  subscribe(
    handler: (message: PresenceMessage) => void,
    subscribeCallback?: (err?: RealtimeError | null) => void
  ): void;
  unsubscribe(): void;
  get(callback: (err: RealtimeError | null, members: Array<PresenceMessage>) => void): void;
}

export enum RealtimeChannelCapability {
  PRESENCE = 'presence',
  SUBSCRIBE = 'subscribe',
}

/**
 * Error class for all realtime errors. Passes through Ably error status codes where applicable, i.e. when
 * errors originate within the Ably SDK.
 */
export class RealtimeError extends ErrorSubclass implements Partial<Ably.Types.ErrorInfo> {
  statusCode?: number;
  constructor(message: string, statusCode?: number) {
    super(message);
    this.statusCode = statusCode;
  }
}

export interface PresenceMessage {
  type: PresenceMessageType;
  userId: string;
  connectionId: string;
}

export enum PresenceMessageType {
  ENTER = 'enter',
  LEAVE = 'leave',
  PRESENT = 'present',
}

/**
 * This is the definition of all message data types.
 *
 * We use a map to ensure that each message type is present exactly once.
 */
interface RealtimeMessageData {
  [RealtimeMessageType.ENTRY_UPDATE]: Partial<{
    clientId: string;
  }>;
  [RealtimeMessageType.LOCK_UPDATE]: Partial<{
    clientId: string;
    lock: ServerDocumentEditorLock;
    userNotifications?: Array<DocumentUpdateNotification>;
  }>;
  [RealtimeMessageType.TABLE_API_PENDING_ROWS_UPDATE]: Partial<{
    clientId: string;
    structuredTableApiId: string;
  }>;
  [RealtimeMessageType.WORKSHEET_UPDATE]: Partial<{
    clientId: string;
  }>;
  [RealtimeMessageType.WORKSHEET_STEP_REOPENED]: {
    clientId: string;
    stepName: string;
  };
  [RealtimeMessageType.REVIEW_UPDATE]: Partial<{
    clientId: string;
  }>;
  [RealtimeMessageType.CHAT_NAME_UPDATED]: {
    chatId: string;
    name: string;
    modifiedAt: string;
  };
  [RealtimeMessageType.LLM_RESPONSE_CHUNK]: {
    chatId: string;
    userEventId: string;
    chunkNumber: number;
    content: string;
  };
  [RealtimeMessageType.LLM_RESPONSE_COMPLETE]: {
    chatId: string;
    userEventId: string;
    totalChunks: number;
    content: string;
  };
}

export type EncryptedRealtimeMessage = {
  [K in RealtimeMessageType]: {
    name: K;
    data: {
      ciphertext: string;
    };
  };
}[RealtimeMessageType];

/**
 * This is the definition of all message types.
 */
export type RealtimeMessage = {
  [K in RealtimeMessageType]: {
    name: K;
    data: RealtimeMessageData[K];
  };
}[RealtimeMessageType];

/**
 * Retrieve a particular message type.
 */
export type GetRealtimeMessage<T extends RealtimeMessageType> = Extract<RealtimeMessage, {name: T}>;

/**
 * Retrieve the data for a particular message type.
 */
export type GetRealtimeMessageData<T extends RealtimeMessageType> = GetRealtimeMessage<T>['data'];
